import {
  addMemberWidgetToGroupWidget,
  addNewWidgetAndReplaceExisting,
  removeMemberWidgetFromGroupWidget,
  store,
  updateWidgetAndMoveBehindTarget,
  useAppDispatch,
  useAppSelector,
} from 'modules/Editor/store';
import { UpdateWidget, updateWidget } from 'modules/Editor/store/infographSlice';
import {
  selectActiveWidgets,
  selectCropId,
  selectIsActiveWidget,
  selectIsAllWidgetsSelectedSameType,
  selectIsWholeGroupSelected,
} from 'modules/Editor/store/widgetSelector';
import { createContext, useCallback, useContext, useRef } from 'react';
import { WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { Subset } from 'types/object.types';
import { Widget, WidgetType } from 'types/widget.types';
import { proxy, useSnapshot } from 'valtio';
import { useInfographStateRenderer } from 'widgets/InfographStateRendererContext';
import { SAME_WIDGET_BATCH } from 'widgets/Widget.config';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { MultiEditToolbarWidgetTypes, NewWidget, ReadOnlyRenderType } from 'widgets/Widget.types';
import { RenderType } from 'widgets/WidgetRenderer.types';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';
import { getAllMultiEditWidgetIds } from 'widgets/sdk/Toolbar/MultiWidgetToolbar/MultiWidgetToolbar.helpers';
import { TOP_LAYER_Z_INDEX } from 'widgets/sdk/widgetSdk.config';

import { responsiveGroupBatchUpdate } from './ResponsiveGroup/ResponsiveGroupBatchUpdate';

type StoreWidget = {
  renderType?: RenderType;
  widgetId: WidgetId;
  isWidgetSelected: boolean;
  updateWidget: (data: Partial<Widget>) => void;
  replaceWidget: (newWidget: NewWidget) => void;
  addMemberWidgetToGroup: (newMemberWidget: NewWidget, data?: Partial<Widget>) => void;
  removeMemberWidgetFromGroup: (memberWidgetId: WidgetId, data?: Partial<Widget>) => void;
  updateWidgetAndMoveBehind: (data: Partial<Widget>, targetWidgetId: WidgetId) => void;
} & Widget;

interface WidgetStoreI {
  widget: StoreWidget;
}

type WidgetStoreProps = {
  widgetId: WidgetId;
  zIndex?: number;
  renderType?: RenderType;
  // The customWidgetData is used to override the default widget data
  // This is needed for responsive widgets, where the base widget data can be different
  customWidgetData?: Partial<Widget>;
  children: React.ReactNode;
};

const defaultContextValues = {
  widgetId: '',
  isWidgetSelected: false,
  updateWidget: (data: Subset<Widget>) => {},
  replaceWidget: (newWidget: NewWidget) => {},
  addMemberWidgetToGroup: (newMemberWidget: NewWidget, data?: Subset<Widget>) => {},
  removeMemberWidgetFromGroup: (memberWidgetId: WidgetId, data?: Subset<Widget>) => {},
  updateWidgetAndMoveBehind: (data: Subset<Widget>, targetWidgetId: WidgetId) => {},
  topPx: 0,
  leftPx: 0,
  version: 0,
  widthPx: 0,
  heightPx: 0,
  rotateDeg: 0,
};

const WidgetStoreContext = createContext<WidgetStoreI>({
  widget: defaultContextValues,
});

/**
 * A hook to connect to the widget store
 * This is an internal component, and should only be used for internal widget sdk purposes.
 */
export const useWidgetStore = () => {
  const widgetStore = useContext(WidgetStoreContext);
  const widgetStoreSnapshot = useSnapshot(widgetStore);

  return widgetStoreSnapshot.widget;
};

/**
 * A React Context Provider that provides the widget data through a reactive proxy.
 * The store is kept in sync with the widget in the infograph.
 * This is an internal component, and should only be used for internal widget sdk purposes.
 */
export const WidgetStore = ({
  widgetId,
  zIndex: baseZIndex,
  customWidgetData,
  children,
  renderType,
}: WidgetStoreProps) => {
  const dispatch = useAppDispatch();
  // widget data subscription from redux
  const wData = useAppSelector((state) => state.infograph.widgets[widgetId]);

  const isWidgetSelected = useAppSelector(selectIsActiveWidget(widgetId));

  /**
   * Wrapper to update the widget data
   * @param widgetData changes for widgetId
   * @param moreUpdates optional additional widget updates to batch together in the same commit as widgetData
   */
  const onUpdateWidget = useCallback(
    (widgetData: Partial<Widget>, moreUpdates?: UpdateWidget[]) => {
      // batch update all widgets if they belong the the same widget type list
      const widgetType = getWidgetTypeFromId(widgetId);
      if (SAME_WIDGET_BATCH[widgetType as MultiEditToolbarWidgetTypes]) {
        if (selectIsAllWidgetsSelectedSameType(store.getState())) {
          const activeWidgets = selectActiveWidgets(store.getState());
          const allSelectedIds = getAllMultiEditWidgetIds(activeWidgets);

          const batchSelectedUpdate: UpdateWidget[] = allSelectedIds
            .map((id) => [{ widgetId: id, widgetData }, ...(moreUpdates || [])])
            .flat();
          dispatch(updateWidget(batchSelectedUpdate));

          return;
        }

        // Special case: Handle when a whole group is selected with multiple images
        if (selectIsWholeGroupSelected(store.getState())) {
          const state = store.getState();
          const activeWidgets = selectActiveWidgets(state);

          // Only for image widgets in groups
          if (widgetType === WidgetType.Image && activeWidgets.length === 1) {
            const imageWidgetsInGroup = activeWidgets[0].groupMembers.filter(
              (id) => getWidgetTypeFromId(id) === WidgetType.Image,
            );

            if (imageWidgetsInGroup.length > 1) {
              const batchUpdates: UpdateWidget[] = imageWidgetsInGroup
                .map((id) => [{ widgetId: id, widgetData }, ...(moreUpdates || [])])
                .flat();

              dispatch(updateWidget(batchUpdates));
              return;
            }
          }
        }

        // Handle multi-selection with multiple image widgets (not in a group)
        if (widgetType === WidgetType.Image) {
          const state = store.getState();
          const activeWidgets = selectActiveWidgets(state);

          if (activeWidgets.length > 1) {
            const imageWidgetsInSelection = activeWidgets
              .filter((widget) => getWidgetTypeFromId(widget.id) === WidgetType.Image)
              .map((widget) => widget.id);

            if (imageWidgetsInSelection.length > 1) {
              const batchUpdates: UpdateWidget[] = imageWidgetsInSelection
                .map((id) => [{ widgetId: id, widgetData }, ...(moreUpdates || [])])
                .flat();

              dispatch(updateWidget(batchUpdates));
              return;
            }
          }
        }
      }

      // For widgets in a responsive group - multiple widget updates might be batched together here
      // e.g. a text widget in a group changes height - move the other widgets in the group below down
      // The change is batched so it can be considered as one history step
      const parentId = groupIdCache.getParentIdNested(widgetId);
      const batchUpdate = responsiveGroupBatchUpdate.getBatchUpdate(parentId);

      if (batchUpdate && batchUpdate.length) {
        const newUpdate = [...batchUpdate, { widgetId, widgetData }, ...(moreUpdates || [])];
        dispatch(updateWidget(newUpdate));

        // reset the batch update after
        responsiveGroupBatchUpdate.clear(parentId);
        return;
      }

      dispatch(updateWidget([{ widgetId, widgetData }, ...(moreUpdates || [])]));
    },
    [dispatch, widgetId],
  );

  const onReplaceWidget = useCallback(
    (newWidgetData: NewWidget) => {
      dispatch(addNewWidgetAndReplaceExisting({ widgetId, newWidgetData }));
    },
    [dispatch, widgetId],
  );

  const onAddMemberWidgetToGroup = useCallback(
    (newMemberWidgetData: NewWidget, data?: Partial<Widget>) => {
      dispatch(addMemberWidgetToGroupWidget({ widgetId, newMemberWidgetData, data }));
    },
    [dispatch, widgetId],
  );

  const onRemoveMemberWidgetFromGroup = useCallback(
    (memberWidgetId: WidgetId, data?: Partial<Widget>) => {
      dispatch(removeMemberWidgetFromGroupWidget({ widgetId, memberWidgetId, data }));
    },
    [dispatch, widgetId],
  );

  const onUpdateWidgetAndMoveBehind = useCallback(
    (widgetData: Partial<Widget>, targetWidgetId: WidgetId, moreUpdate?: UpdateWidget[] | undefined) => {
      dispatch(updateWidgetAndMoveBehindTarget({ widgetId, widgetData, targetWidgetId }));
    },
    [dispatch, widgetId],
  );

  // If a widget is in crop view -> render the widget on top of all other page widgets
  const cropId = useAppSelector(selectCropId);
  const isCropping = widgetId === cropId;
  const zIndex = isCropping ? TOP_LAYER_Z_INDEX : baseZIndex;

  const widget = {
    ...wData,
    zIndex,
    ...customWidgetData,
    widgetId,
    renderType,
    isWidgetSelected,
    updateWidget: onUpdateWidget,
    replaceWidget: onReplaceWidget,
    addMemberWidgetToGroup: onAddMemberWidgetToGroup,
    removeMemberWidgetFromGroup: onRemoveMemberWidgetFromGroup,
    updateWidgetAndMoveBehind: onUpdateWidgetAndMoveBehind,
  };
  const state = useRef<WidgetStoreI>(proxy({ widget })).current;

  // sync widget store with redux
  state.widget = widget;

  const { infographState } = useInfographStateRenderer() ?? {};

  if (infographState) {
    return (
      <WidgetStoreInfographState
        widgetId={widgetId}
        zIndex={baseZIndex}
        customWidgetData={customWidgetData}
        infographState={infographState}
      >
        {children}
      </WidgetStoreInfographState>
    );
  }

  return <WidgetStoreContext.Provider value={state}>{children}</WidgetStoreContext.Provider>;
};

type WidgetStoreInfographStateProps = {
  children: React.ReactNode;
  infographState: InfographState;
  widgetId: WidgetId;
  zIndex?: number;
  customWidgetData?: Partial<Widget>;
};

export const WidgetStoreInfographState = ({
  children,
  infographState,
  widgetId,
  zIndex,
  customWidgetData,
}: WidgetStoreInfographStateProps) => {
  const wData = infographState?.widgets?.[widgetId];

  const widget = {
    ...defaultContextValues,
    ...wData,
    zIndex,
    ...customWidgetData,
    widgetId,
    renderType: ReadOnlyRenderType.ThumbnailReadOnly,
  };

  const state = useRef<WidgetStoreI>(proxy({ widget })).current;

  // sync widget store with redux
  state.widget = widget;

  return <WidgetStoreContext.Provider value={state}>{children}</WidgetStoreContext.Provider>;
};
